/**
 * The registry's shape and the one function that turns a route into a URL. Kept apart from the
 * registries so a plane's bundle can build a path without pulling in another plane's schemas.
 */

import type { ZodType } from 'zod';

type ParamsOf<P extends string> = P extends `${string}$${infer Name}/${infer Rest}`
  ? Name | ParamsOf<`/${Rest}`>
  : P extends `${string}$${infer Name}`
    ? Name
    : never;

export interface FreeRoute<P extends string, T> {
  path: P;
  schema: ZodType<T>;
  spends: false;
}

export interface SpendingRoute<P extends string, T> {
  path: P;
  schema: ZodType<T>;
  spends: true;
  free: Readonly<Record<string, string>>;
}

export type GetRoute = FreeRoute<string, unknown> | SpendingRoute<string, unknown>;

export function free<const P extends string, T>(path: P, schema: ZodType<T>): FreeRoute<P, T> {
  return { path, schema, spends: false };
}

export function spending<const P extends string, T>(
  path: P,
  schema: ZodType<T>,
  whenFree: Readonly<Record<string, string>>,
): SpendingRoute<P, T> {
  return { path, schema, spends: true, free: whenFree };
}

/** Substitutes each `$name` segment and appends `query`; an empty query adds no `?`. */
export function routePath<P extends string>(
  route: { path: P },
  params: { [K in ParamsOf<P>]: string },
  query?: URLSearchParams | Record<string, string>,
): string {
  const values: Record<string, string> = params;
  const path = route.path.replace(/\$(\w+)/g, (_, name: string) =>
    encodeURIComponent(values[name] ?? ''),
  );
  const search = new URLSearchParams(query).toString();
  return search === '' ? path : `${path}?${search}`;
}
